import { useCallback, useEffect, useState } from 'react'
import useUser from './useUser'

/**
 * Has this user already seen the current "what's new" announcement?
 *
 * Drives two things that must agree: whether the pop-up fires on login, and
 * whether the sidebar's "What's new" row carries a count. They read the same
 * hook so a dismissal clears both.
 *
 * ── WHY localStorage ────────────────────────────────────────────────────────
 *
 * Morpheus has no user-preferences field — the user comes from `/members` with
 * nothing to write back to — so the alternative was blocking this on an API
 * change in someone else's queue. The cost is real and worth knowing: this is
 * per-BROWSER, so someone on a laptop and an iPad meets the pop-up twice, and
 * clearing site data brings it back. Accepted, because the pop-up is a nudge
 * rather than anything transactional. If a preferences endpoint ever lands,
 * this hook is the one place to change.
 *
 * Key convention follows `use-feature-flags.ts` (`connect:newUi:enabled`).
 *
 * Keyed by BOTH:
 *   • uid — a shared back-office machine re-shows for the next person to log
 *     in, rather than one manager silently dismissing it for the whole venue.
 *   • announcement id — bumping the constant re-announces to everyone without
 *     touching this file's logic or hunting stale keys.
 */

/**
 * Bump to announce something new. Old keys are simply never read again.
 *
 * Bumped WITH `ANNOUNCED_FEATURES` in the shared block, always: the pop-up
 * renders whatever that array holds, so swapping the three without bumping this
 * would show the new set only to people who never dismissed the old one.
 */
export const ANNOUNCEMENT_ID = '2026-09-three-features'

const keyFor = (uid: string) =>
  `connect:whats-new:seen:${uid}:${ANNOUNCEMENT_ID}`

/**
 * Every access is guarded. Safari in private mode throws on `localStorage`
 * rather than returning null, and an announcement banner is not worth taking
 * the whole org layout down over.
 */
function readSeen(uid: string | undefined): boolean {
  if (!uid || typeof window === 'undefined') return false
  try {
    return window.localStorage.getItem(keyFor(uid)) === 'true'
  } catch {
    // No storage: treat as seen. Showing a pop-up that can never be dismissed
    // on every single navigation is far worse than never showing it.
    return true
  }
}

export function useWhatsNewSeen(): {
  /** True until the user dismisses. `undefined` uid reads as seen. */
  seen: boolean
  markSeen: () => void
} {
  const uid = useUser()?.uid
  const [seen, setSeen] = useState<boolean>(() => readSeen(uid))

  // The user query resolves after first render, so the initial state above is
  // computed with no uid. Re-read once it arrives.
  useEffect(() => {
    setSeen(readSeen(uid))
  }, [uid])

  const markSeen = useCallback(() => {
    setSeen(true)
    if (!uid) return
    try {
      window.localStorage.setItem(keyFor(uid), 'true')
    } catch {
      // Dismissal still holds for this session via state above.
    }
  }, [uid])

  return { markSeen, seen }
}
